import AssetLoader from "@/components/AssetLoader/AssetLoader";
import CDNLink from "@/components/CDNLink/CDNLink";
import PDFPreview from "@/components/PDFViewer/PDFPreview";
import StructLoader from "@/components/StructLoader/StructLoader";
import DebugDataComponent from "@/debug/DebugDataComponent";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import { CurrencyLabelOverlayComponent } from "@/modules/abstract-ui/forms/input/CurrencyLabel";
import BFOverlay from "@/modules/abstract-ui/general/whisper/BFOverlay";
import BfIcon from "@/modules/abstract-ui/icon/BfIcon";
import { ActivitiesLabel } from "@/modules/activity-components/ActivityLabel/ActivityLabel";
import ContactLabel from "@/modules/contacts-module/ContactLabel";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import LanguageService from "@/services/LanguageService";
import StringUtils from "@/utils/StringUtils";
import classNames from "classnames";
import { AssetCashBudgetEntry } from "../../cashBudget/model/CashBudgetEntry";
import InvoiceService from "../InvoiceService";
import { Invoice } from "../RAInterfaces";
import RAInvoiceDirectionIndicator from "./InvoiceForm/RAInvoiceDirectionIndicator";
import "./InvoiceView.scss";
import InvoicePaymentPlan, {
  InvoicePaymentDataTable,
} from "./ListInvoices/InvoicePaymentPlan";

interface InvoiceViewProps {
  invoice: Invoice;
  compareBooking?: AssetCashBudgetEntry;
}
export const InvoiceViewLoader = (props: {
  id: string;
  compareBooking?: AssetCashBudgetEntry;
}) => {
  return (
    <AssetLoader
      id={props.id}
      assetType={AssetTypes.Invoicing.Invoice}
      render={(invoice: Invoice) => (
        <InvoiceView invoice={invoice} compareBooking={props.compareBooking} />
      )}
    />
  );
};
const InvoiceView = (props: InvoiceViewProps) => {
  const invoice = props.invoice;
  const differentCurrency =
    invoice.data.invoice.value.currency !==
    invoice.data.invoice.value.converted.currency;
  const hasMultiplePayments = invoice.data.payment?.length > 1;

  const showAmountOverlay = differentCurrency || hasMultiplePayments;

  const amountValue = (
    <div className={`invoice-amount  __font-md __weight-500 `}>
      <span>
        {StringUtils.formatCurrency(
          invoice.data.invoice.value.amount,
          null,
          null,
          invoice.data.invoice.value.currency
        )}
      </span>
      {showAmountOverlay && (
        <BfIcon data={"information-circle"} type="light" size="xxs" />
      )}
    </div>
  );
  const openAmount = invoice.data.opos?.openSum7Days || 0;

  const cdnOriginal = invoice.data.invoice.file;
  const cdnDataOriginal = {
    filename: InvoiceService.generateFilenameForInvoice(invoice),
    assetField: "data.invoice.file",
    assetId: invoice._id,
    assetType: "invoice",
    cdnData: cdnOriginal,
    cdnId: cdnOriginal.id,
  };
  return (
    <StructLoader
      structTypes={["invoice", "orga"]}
      unitType={props.invoice.data.type}
      render={() => (
        <div className={classNames(`invoice-view-container`)}>
          {props.compareBooking && (
            <div className={`suggestion-indicator-container`}>
              <div className={`suggestion-indicator`} />
            </div>
          )}
          <div className={`invoice-pdf-preview-container`}>
            <CDNLink
              assetType={cdnDataOriginal.assetType}
              assetId={cdnDataOriginal.assetId}
              assetField={cdnDataOriginal.assetField}
              cdnId={cdnDataOriginal.cdnId}
              filename={cdnDataOriginal.cdnData.filename}
              fileKey={cdnDataOriginal.cdnData.key}
              hasFolderReadPermissions={true}
              render={(url) => <PDFPreview url={url} width={60} height={90} />}
            />
          </div>
          <div className={`invoice-view-fields-container`}>
            <div className={`__flex`}>
              <RAInvoiceDirectionIndicator
                marginBottom={0}
                small
                direction={props.invoice.data.direction}
                kind={props.invoice.data.kind}
              />
              <div className={`invoice-type`}>
                {LanguageService.translateLabel(
                  InvoiceStruct.getInvoiceType(invoice.data.invoice.invoiceType)
                    ?.data.displayName
                )}
              </div>
            </div>
            <div className={`__sub-text __flex`}>
              <DebugDataComponent data={invoice} />
              <div>
                {StringUtils.formatDate(invoice.data.invoice.documentDate)}
              </div>
              <div>{invoice.data.invoice.invoiceId}</div>
            </div>
            <div className={`contact-name __font-md __weight-500`}>
              <ContactLabel contactId={invoice.data.invoice.contact} />
            </div>
            <div className={`activities`}>
              <ActivitiesLabel activities={invoice.data.activity} />
            </div>
          </div>
          <div className={`invoice-amount-fields-container`}>
            {showAmountOverlay ? (
              <BFOverlay
                trigger="hover"
                enterable
                speakerFC={(close) => (
                  <div className={`padding-10`}>
                    {differentCurrency && (
                      <div>
                        <CurrencyLabelOverlayComponent
                          value={invoice.data.invoice.value}
                          showConverted={true}
                        />
                      </div>
                    )}
                    {differentCurrency && hasMultiplePayments && <hr />}
                    {hasMultiplePayments && (
                      <div>
                        <InvoicePaymentDataTable
                          asset={props.invoice}
                          type="payment"
                        />
                      </div>
                    )}
                  </div>
                )}
              >
                {amountValue}
              </BFOverlay>
            ) : (
              amountValue
            )}
            <OpenAmountLabel {...props} />
            <InvoicePaymentPlan asset={props.invoice} type="installment" />
          </div>
        </div>
      )}
    />
  );
};

export default InvoiceView;

const OpenAmountLabel = (props: InvoiceViewProps) => {
  const invoice = props.invoice;

  const openAmountNow = invoice.data.opos?.openSum7Days || 0;
  const openAmountAllTime = invoice.data.opos?.openSum || 0;
  if (openAmountNow > 0) {
    return (
      <div className={`open-amount-label warn`}>
        {i18n.t(
          "ra:InvoiceView.OpenAmountLabel.openAmountNowDemand",
          "Zu Bezahlen: {{amount}}",
          {
            amount: StringUtils.formatCurrency(
              openAmountNow,
              null,
              null,
              invoice.data.invoice.value.currency
            ),
          }
        )}
      </div>
    );
  } else if (openAmountNow < 0) {
    return (
      <div className={`open-amount-label positive`}>
        {i18n.t(
          "ra:InvoiceView.OpenAmountLabel.openAmountNowCredit",
          "Offene Gutschrift: {{amount}}",
          {
            amount: StringUtils.formatCurrency(
              Math.abs(openAmountNow),
              null,
              null,
              invoice.data.invoice.value.currency
            ),
          }
        )}
      </div>
    );
  } else {
    if (openAmountAllTime === 0) {
      return (
        <div className={`open-amount-label neutral`}>
          {i18n.t(
            "ra:InvoiceView.OpenAmountLabel.noOpenAmount",
            "Rechnung vollständig bezahlt"
          )}
        </div>
      );
    } else {
      return (
        <div className={`open-amount-label future neutral`}>
          {i18n.t(
            "ra:InvoiceView.OpenAmountLabel.noOpenAmountFuture",
            "Demnächst fällig: {{amount}}",
            {
              amount: StringUtils.formatCurrency(
                openAmountAllTime,
                null,
                null,
                invoice.data.invoice.value.currency
              ),
            }
          )}
        </div>
      );
    }
  }
};
